import { requestBlob, requestJson } from "services/http";

const jsonOptions = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export const getGreenhouseDevices = () =>
  requestJson("/api/padihat/greenhouse/devices/");

export const getGreenhouseDeviceData = (mac, limit = 100) =>
  requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/data?limit=${limit}`
  );

export const getGreenhouseTelemetrySeries = (mac, { runId, limit = 2000 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (runId) params.set("run_id", runId);
  return requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/telemetry-series/?${params.toString()}`
  );
};

// KIT0139 pressure/level sensor bring-up readings - time-window (start/end)
// scoped like PadiHAT's own getTelemetrySeries, NOT run_id-filtered like
// getGreenhouseTelemetrySeries above: this sensor's MQTT payload never
// carries a run_id, so there's nothing to filter by on that axis (see
// padihat-backend's greenhouse_level_sensor_series docstring for why).
export const getGreenhouseLevelSensorSeries = (mac, { start, end, limit = 2000 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  return requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/level-sensor-series/?${params.toString()}`
  );
};

// Browsable run history for one controller (RunHistoryList) - mirrors
// greenhouse_run_list's pagination contract (limit default 50, offset
// default 0), most-recent-first.
export const listGreenhouseRuns = (mac, { limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/runs/?${params.toString()}`
  );
};

export const startGreenhouseRun = (
  mac,
  {
    runId,
    label,
    profile,
    profileId,
    linkedPadihatRunId,
    linkedCameraRunId,
    waterDeviceMac,
    sampleMs,
    distanceSamples,
    waterEvents,
    waterStartLevelCm,
    tankMinCm,
    tankMaxCm,
  }
) =>
  requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/start-run/`,
    jsonOptions("POST", {
      run_id: runId,
      label,
      profile_id: profileId || null,
      profile,
      linked_padihat_run_id: linkedPadihatRunId || "",
      linked_camera_run_id: linkedCameraRunId || "",
      water_device_mac: waterDeviceMac || "",
      // Optional water-device (PDH_LAB_WIFI) sampling override - backend
      // treats null/absent identically ("not overridden"), so sending
      // null when unset is fine and simpler than conditionally omitting
      // the key, matching profile_id's existing pattern above.
      sample_ms: sampleMs ?? null,
      distance_samples: distanceSamples ?? null,
      // Scheduled pump operations (the backend runs them; never sent to the firmware). The tank
      // setup is what the backend validates them against.
      water_events: waterEvents || [],
      water_start_level_cm: waterStartLevelCm ?? null,
      tank_min_cm: tankMinCm ?? null,
      tank_max_cm: tankMaxCm ?? null,
    })
  );

export const stopGreenhouseRun = (mac, { waterDeviceMac } = {}) =>
  requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/stop-run/`,
    jsonOptions("POST", { water_device_mac: waterDeviceMac || "" })
  );

// Pump control - independent of the stress-run lifecycle above (no
// interlock, by the firmware's own design), so these don't take any
// run-related fields.
// Fill/drain are by DISTANCE: the pump runs cm * (a measured 100 s/cm since firmware 1.2.2, owned by
// the firmware - see pump_config on GET devices) with no water-level input.
export const fillPump = (mac, { cm }) =>
  requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/pump-fill/`,
    jsonOptions("POST", { cm })
  );

export const drainPump = (mac, { cm }) =>
  requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/pump-drain/`,
    jsonOptions("POST", { cm })
  );

export const stopPump = (mac) =>
  requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/pump-stop/`,
    jsonOptions("POST", {})
  );

export const listStressProfiles = ({ includeArchived } = {}) => {
  const params = new URLSearchParams();
  if (includeArchived) params.set("include_archived", "1");
  const qs = params.toString();
  return requestJson(`/api/padihat/greenhouse/profiles/${qs ? `?${qs}` : ""}`);
};

export const getStressProfile = (id) =>
  requestJson(`/api/padihat/greenhouse/profiles/${encodeURIComponent(id)}/`);

export const createStressProfile = ({ name, description, steps, waterEvents }) =>
  requestJson(
    "/api/padihat/greenhouse/profiles/",
    jsonOptions("POST", { name, description: description || "", steps, water_events: waterEvents || [] })
  );

export const updateStressProfile = (id, { name, description, steps, waterEvents }) =>
  requestJson(
    `/api/padihat/greenhouse/profiles/${encodeURIComponent(id)}/`,
    jsonOptions("PATCH", { name, description, steps, water_events: waterEvents })
  );

// The controller's pump operations (scheduled and manual), oldest first, with the before/after
// photos linked to each. JWT-authed (it returns camera photos).
export const getWaterEvents = (mac, { runId, limit } = {}) => {
  const params = new URLSearchParams();
  if (runId) params.set("run_id", runId);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return requestJson(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/water-events/${qs ? `?${qs}` : ""}`
  );
};

export const archiveStressProfile = (id) =>
  requestJson(`/api/padihat/greenhouse/profiles/${encodeURIComponent(id)}/`, { method: "DELETE" });

const safeFilename = (value) =>
  (value || "greenhouse-run-export.zip").replace(/[^A-Za-z0-9._-]/g, "_");

// Mirrors cameraApi.js's downloadCameraRun: fetch the zip as an
// authenticated blob, name it from the server's Content-Disposition (with a
// sensible fallback), and save it via a throwaway object-URL anchor.
export async function exportGreenhouseRun(mac, runId, { waterDeviceMac } = {}) {
  const params = new URLSearchParams();
  if (waterDeviceMac) params.set("water_device_mac", waterDeviceMac);
  const qs = params.toString();
  const { blob, contentDisposition } = await requestBlob(
    `/api/padihat/greenhouse/devices/${encodeURIComponent(mac)}/runs/${encodeURIComponent(runId)}/export/${qs ? `?${qs}` : ""}`
  );
  const encoded = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
  const filename = safeFilename(
    encoded ? decodeURIComponent(encoded[1]) : plain ? plain[1] : `greenhouse_run_${runId}.zip`
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
